import { Component, useEffect, useRef, useState, type ReactNode } from 'react';

/** Shared presentation primitives. Deliberately small and unopinionated. */

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'info' | ''> = {
  PAYOUT_SUCCESS: 'ok',
  APPROVED: 'info',
  KYC_PASSED: 'info',
  PAYOUT_INITIATED: 'info',
  RECEIVED: '',
  KYC_PENDING: '',
  KYC_IN_PROGRESS: '',
  FRAUD_REVIEW: '',
  FLAGGED: 'warn',
  FUNDS_HELD: 'warn',
  PAYOUT_FAILED: 'warn',
  KYC_FAILED: 'danger',
  REJECTED: 'danger',
  EXPIRED: 'danger',
};

/** Human-readable, but the machine status stays visible in the title. */
const STATUS_LABEL: Record<string, string> = {
  RECEIVED: 'Received',
  KYC_PENDING: 'Awaiting identity',
  KYC_IN_PROGRESS: 'KYC running',
  KYC_PASSED: 'KYC passed',
  KYC_FAILED: 'KYC failed',
  FRAUD_REVIEW: 'Fraud review',
  FLAGGED: 'Flagged',
  APPROVED: 'Approved',
  PAYOUT_INITIATED: 'Paying',
  PAYOUT_FAILED: 'Payout failed',
  FUNDS_HELD: 'Funds held',
  PAYOUT_SUCCESS: 'Paid',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
};

export function Status({ value }: { value: string }) {
  return (
    <span className={`pill ${STATUS_TONE[value] ?? ''}`} title={value}>
      {STATUS_LABEL[value] ?? value}
    </span>
  );
}

export function Card({ title, action, children, flush }: { title?: string; action?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <section className="card">
      {(title || action) && (
        <header className="card-head">
          {title && <h2>{title}</h2>}
          <div className="spacer" />
          {action}
        </header>
      )}
      <div className={`card-body${flush ? ' flush' : ''}`}>{children}</div>
    </section>
  );
}

export function Kpi({ label, value, foot, small }: { label: string; value: ReactNode; foot?: ReactNode; small?: boolean }) {
  return (
    <div className="card kpi">
      <div className="label">{label}</div>
      <div className={`value${small ? ' sm' : ''}`}>{value}</div>
      {foot && <div className="foot">{foot}</div>}
    </div>
  );
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="table-empty">
      <div className="big">{title}</div>
      {hint && <div className="small">{hint}</div>}
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading">
      <span className="spin" /> <span style={{ marginLeft: 8 }}>{label}…</span>
    </div>
  );
}

export function Banner({ tone, title, children, action }: { tone: 'ok' | 'warn' | 'danger' | 'info'; title?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className={`banner ${tone}`}>
      <div className="body">
        {title && <strong>{title}. </strong>}
        {children}
      </div>
      {action}
    </div>
  );
}

export function Modal({ title, description, children, footer, onClose }: {
  title: string; description?: string; children: ReactNode; footer: ReactNode; onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  /*
   * The latest onClose, without it being an effect dependency.
   *
   * Callers pass an inline arrow, so `onClose` is a new function on every
   * render. Depending on it re-ran the effect after every keystroke — and the
   * focus call below then pulled focus off whatever field was being typed
   * into, so a textarea would accept exactly one character. Both effects below
   * are mount-only for that reason.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    // Land the caret where the work is. Prefer the first field — most of these
    // dialogs exist to collect a written reason — and fall back to the dialog
    // itself so a keyboard user is never left outside it.
    const field = ref.current?.querySelector<HTMLElement>('input:not([type=hidden]), textarea, select');
    (field ?? ref.current)?.focus();
  }, []);

  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <div className="modal-head">
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">{footer}</div>
      </div>
    </div>
  );
}

/** Naira from a kobo string. The engine already sends a display value; this is
 *  for the few places the dashboard composes its own figure. */
export function formatKobo(kobo: string): string {
  const negative = kobo.startsWith('-');
  const digits = (negative ? kobo.slice(1) : kobo).padStart(3, '0');
  const major = digits.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}₦${major}.${digits.slice(-2)}`;
}

export function parseNairaToKobo(input: string): string | null {
  const clean = input.replace(/[,₦\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return null;
  const [major, minor = ''] = clean.split('.');
  return `${BigInt(major!) * 100n + BigInt(minor.padEnd(2, '0'))}`;
}

export function when(iso: string, opts: { time?: boolean } = {}): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return opts.time === false ? date : `${date}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

export function ago(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** Data loader with an explicit refresh handle. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): {
  data: T | null; error: string | null; loading: boolean; reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fn()
      .then((d) => { if (live) { setData(d); setError(null); } })
      .catch((e: Error) => { if (live) setError(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}

/**
 * Error boundary.
 *
 * A render error in one screen should not blank the whole console. An ops tool
 * is used under pressure — someone chasing a stuck payout needs the navigation
 * to survive a component that failed, and needs to be told what broke rather
 * than shown an empty page.
 */
export class Boundary extends Component<{ children: ReactNode; onReset?: () => void }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: unknown) {
    // eslint-disable-next-line no-console
    console.error('screen crashed', error, info);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <Banner
        tone="danger"
        title="This screen failed to render"
        action={
          <button
            className="btn sm"
            onClick={() => {
              this.setState({ error: null });
              this.props.onReset?.();
            }}
          >
            Try again
          </button>
        }
      >
        {this.state.error.message}. Everything else still works — the navigation on the left is unaffected.
      </Banner>
    );
  }
}
